<template>
  <v-dialog 
    v-model="dialog" 
    max-width="95%"
    width="1400px"
    @click:outside="closeDialog" 
    content-class="add-user-dialog"  
    persistent
  >
    <v-card rounded="0">
      <!-- Header -->
      <v-toolbar color="#117443" class="white--text sticky-toolbar" flat :elevation="0">
        <v-toolbar-title class="font-weight-bold white--text">
          {{ $t("farm.addNewUsers") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Main Content -->
      <v-card-text class="pt-5 px-5 pb-5">
        <!-- Section Title -->
        <h2 class="font-weight-bold mb-2">{{ currentStep === 1 ? $t("farm.addUsersToFarm") : $t("farm.selectedUsers") }}</h2>
        <p class="mb-5 text--secondary">
          {{ currentStep === 1 
            ? $t("farm.selectTheUsersToAddInFarmFromTheBelowListOfAllUserInACooperative")
            : $t("farm.reviewTheSelectedUsersAndAssignTheirAccessRoles")
          }}
        </p>

        <!-- Search and Filter Bar -->
        <div class="d-flex mb-4 align-center flex-wrap">
          <!-- Search Input -->
          <div class="srch mr-2">
            <v-text-field 
              prepend-inner-icon="mdi-magnify" 
              outlined 
              height="5px"
              :placeholder="$t('farm.search')" 
              dense 
              v-model="search" 
              @input="searchQuery"
              class="shrink" 
              style="width: 300px"
            >
            </v-text-field>
          </div>

          <!-- Refresh Button -->
          <v-btn 
            outlined 
            class="mx-2 mb-6" 
            color="grey" 
            style="min-width: 40px; padding: 0;"
            @click="resetSearch"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>

          <!-- Filter Dropdowns -->
          <v-select
            v-model="selectedRole"
            :items="roles"
            item-text="text"
            item-value="value"
            outlined
            dense
            :placeholder="$t('farm.allRoles')"
            class="mx-2"
            style="max-width: 150px; min-width: 120px;"
            @change="filterData"
          ></v-select>


          <v-spacer></v-spacer>

          <!-- Pagination Info -->
          <div class="py-1 title mr-2">
            {{ currentPageStart }} {{ $t("farm.of") }} {{ currentStep === 1 ? totalUsers : totalSelectedUsers }}
          </div>

          <!-- Pagination Arrows -->
          <v-btn 
            class="mx-2" 
            fab 
            small 
            outlined 
            color="primary" 
            :disabled="options.page <= 1"
            @click="pageChange(false)"
          >
            <v-icon dark>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn 
            class="mx-2" 
            fab 
            small 
            outlined 
            color="primary"
            :disabled="options.page >= totalPages" 
            @click="pageChange(true)"
          >
            <v-icon dark>mdi-chevron-right</v-icon>
          </v-btn>
        </div>

        <!-- User Table -->
        <v-data-table
          :headers="currentStep === 1 ? tableHeaders : selectedUsersTableHeaders"
          :items="currentStep === 1 ? users : selectedUsersList"
          :items-per-page="options.itemsPerPage"
          v-model="tableSelection"
          :show-select="currentStep === 1"
          item-key="id"
          hide-default-footer
          :loading="loading"
          :options.sync="options"
          :server-items-length="currentStep === 1 ? totalUsers : totalSelectedUsers"
          :loading-text="$t('farm.loadingData')"
          class="user-selection-table"
        >
          <template v-slot:item.userNameAndId="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.userName }}</div>
              <div class="text-caption text--secondary">({{ $t("farm.id") }}: {{ item.userId }})</div>
            </div>
          </template>

          <template v-slot:item.email="{ item }">
            <span>{{ item.email || $t("farm.na") }}</span>
          </template>

          <template v-slot:item.userRole="{ item }">
            <span>{{ item.userRole }}</span>
          </template>

          <template v-slot:item.registrationDate="{ item }">
            <span>{{ item.registrationDate }}</span>
          </template>

          <template v-slot:item.userRoleAndDate="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.userRole }}</div>
              <div class="text-caption text--secondary">{{ item.registrationDate }}</div>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
              <v-btn small rounded min-width="120" depressed :class="getStatusClass(item.status)"
                  height="40">
                  {{ getStatusText(item.status) }}
              </v-btn>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu location="start">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" small>
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="removeUser(item)">
                  <v-list-item-title>{{ $t("farm.removeAccess") }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Footer Buttons -->
      <v-card-actions class="px-5 pb-5">
        <v-spacer></v-spacer>
        <v-btn 
          outlined 
          color="primary" 
          @click="currentStep === 1 ? closeDialog() : goBack()"
          class="mr-2"
          style="min-width: 120px;"
        >
          {{ $t("farm.cancel") }}
        </v-btn>
        <v-btn 
          color="primary" 
          dark
          @click="currentStep === 1 ? handleNext() : handleConfirm()"
          style="min-width: 120px;"
        >
          {{ currentStep === 1 ? $t("farm.next") : $t("farm.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>

  </v-dialog>
</template>

<script>
import FarmService from '@/_services/FarmService';
import loadingMixin from "@/mixins/LoadingMixin";
export default {
  name: "AddNewUserToFarm",
  components: {
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    farm: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dialog: false,
      currentStep: 1, // 1 = select users, 2 = review selected users
      debounceTimer: null,
      isInitialMount: true,
      search: "",
      selected: [],
      selectedUsersList: [],
      selectedUsersSelected: [],
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      selectedRole: "",
      selectedRegion: "",
      selectedFarm: "",
      roles: [],
      tableHeaders: [],
      selectedUsersTableHeaders: [],
      users: [],
      totalUsers: 0,
      totalSelectedUsers: 0,
      from: 0,
      to: 0,
    };
  },
  computed: {
    tableSelection: {
      get() {
        return this.currentStep === 1 ? this.selected : this.selectedUsersSelected;
      },
      set(value) {
        if (this.currentStep === 1) {
          this.selected = value;
        } else {
          this.selectedUsersSelected = value;
        }
      },
    },
    totalPages() {
      const total = this.currentStep === 1 ? this.totalUsers : this.totalSelectedUsers;
      const itemsPerPage = this.options.itemsPerPage || 10;
      return total > 0 ? Math.ceil(total / itemsPerPage) : 0;
    },
    currentPageStart() {
      const total = this.currentStep === 1 ? this.totalUsers : this.totalSelectedUsers;
      if (total === 0) {
        return '0';
      }
      const itemsPerPage = this.options.itemsPerPage || 10;
      const start = (this.options.page - 1) * itemsPerPage + 1;
      const end = Math.min(this.options.page * itemsPerPage, total);
      if (start === end) {
        return start.toString();
      }
      return `${start}-${end}`;
    },
  },
  watch: {
    value(newVal) {
      this.dialog = newVal;
      if (newVal) {
        // Reset flag when dialog opens to allow options watcher to work properly
        this.isInitialMount = true;
        this.fetchUsers();
      }
    },
    dialog(newVal) {
      this.$emit("input", newVal);
      if (!newVal) {
        this.resetDialog();
      }
    },
    options: {
      handler() {
        if (this.isInitialMount) {
          this.isInitialMount = false;
          return;
        }
        if (this.currentStep === 1) {
          this.fetchUsers();
        } else {
          this.updateSelectedUsersList();
        }
      },
      deep: true,
    },
    currentStep: {
      handler(newVal) {
        if (newVal === 2) {
          this.updateSelectedUsersList();
        }
      },
    },
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },
    resetDialog() {
      this.search = "";
      this.selected = [];
      this.selectedUsersList = [];
      this.selectedUsersSelected = [];
      this.selectedCountry = "";
      this.selectedRegion = "";
      this.selectedFarm = "";
      this.options.page = 1;
      this.currentStep = 1;
      this.isInitialMount = true; // Reset flag when dialog closes
    },
    resetSearch() {
      this.search = "";
      this.selectedCountry = "";
      this.selectedRegion = "";
      this.selectedFarm = "";
      this.options.page = 1;
      if (this.currentStep === 1) {
        this.fetchUsers();
      } else {
        this.updateSelectedUsersList();
      }
    },
    searchQuery() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        if (this.currentStep === 1) {
          this.fetchUsers();
        } else {
          this.updateSelectedUsersList();
        }
      }, 500);
    },
    filterData() {
      this.options.page = 1;
      if (this.currentStep === 1) {
        this.fetchUsers();
      } else {
        this.updateSelectedUsersList();
      }
    },
    pageChange(next) {
      if (next) {
        this.options.page += 1;
      } else {
        this.options.page -= 1;
      }
    },
    async fetchUsers() {
      this.startLoading();
            try {
                const params = {
                    page: this.options.page,
                    limit: this.options.itemsPerPage || 10,
                    availability : 'available',
                    ...(this.search && { search: this.search }),
                    ...(this.selectedRole && { userType: this.selectedRole }),
                };
                
                const response = await FarmService.getFarmUsers(this.farm.id, params);
                
                if (response && response.data) {
                    const apiData = response.data;
                    
                    if (apiData.users && Array.isArray(apiData.users)) {
                        this.users = apiData.users.map(user => ({
                            id: user.id,
                            userId: user.id,
                            userName: user.name || '-',
                            email: user.email || '-',
                            userRole: user.userType || '-',
                            registrationDate: user.registrationDate 
                                ? new Date(user.registrationDate).toLocaleDateString() 
                                : '-',
                            status: user.status || 'active',
                        }));
                        
                        this.totalUsers = apiData.count || 0;
                    } else {
                        this.users = [];
                        this.totalUsers = 0;
                    }
                } else {
                    this.users = [];
                    this.totalUsers = 0;
                }
            } catch (error) {
                console.error('Error loading app users:', error);
                this.$notify({
                    title: this.$t("farm.error"),
                    message: error?.data?.message || this.$t("farm.failedToLoadUsersPleaseTryAgain"),
                    type: "error",
                });
                this.users = [];
                this.totalUsers = 0;
            } finally {
                this.stopLoading();
            }
    },
    getStatusText(status) {
      switch (status) {
        case "active":
          return this.$t("farm.active");
        case "deactivated":
          return this.$t("farm.deactivated");
        default:
          return status || this.$t("farm.na");
      }
    },
    getStatusClass(status) {
      switch (status) {
        case "active":
          return "green-button";
        case "deactivated":
          return "gray-button";
        default:
          return "gray-button";
      }
    },
    handleNext() {
      if (this.selected.length === 0) {
        this.$notify({
          title: this.$t("farm.pleaseSelectAtLeastOneUser"),
          type: "warning",
        });
        return;
      }
      this.currentStep = 2;
      this.options.page = 1; // Reset to first page when switching to step 2
      this.updateSelectedUsersList();
    },
    handleConfirm() {
      if (this.selected.length === 0) {
        this.$notify({
          title: this.$t("farm.noUsersSelected"),
          type: "warning",
        });
        return;
      }

      this.$emit("users-selected", this.selected);
      this.closeDialog();
    },
    goBack() {
      this.currentStep = 1;
      this.options.page = 1;
    },
    updateSelectedUsersList() {
      this.startLoading();
      setTimeout(() => {
        let filtered = [...this.selected];
        
        if (this.search) {
          const searchLower = this.search.toLowerCase();
          filtered = filtered.filter(
            (user) =>
              user.userName.toLowerCase().includes(searchLower) ||
              user.email.toLowerCase().includes(searchLower) ||
              user.userId.toString().includes(searchLower)
          );
        }

        const start = (this.options.page - 1) * this.options.itemsPerPage;
        const end = start + this.options.itemsPerPage;
        this.selectedUsersList = filtered.slice(start, end);
        this.totalSelectedUsers = filtered.length;
        this.from = filtered.length > 0 ? start + 1 : 0;
        this.to = Math.min(end, filtered.length);
        this.stopLoading();
      }, 100);
    },
    removeUser(item) {
      const index = this.selected.findIndex(u => u.id === item.id);
      if (index !== -1) {
        this.selected.splice(index, 1);
        this.updateSelectedUsersList();
        this.$notify({
          title: this.$t("farm.userRemoved"),
          type: "success",
        });
      }
    },
  },
  mounted() {
    // Initialize roles with translations
    this.roles = [
      { text: this.$t("farm.allRoles"), value: "" },
      { text: this.$t("farm.farmer"), value: "farmer" },
      { text: this.$t("farm.technician"), value: "technician" },
    ];
    
    // Initialize table headers with translations
    this.tableHeaders = [
      {
        text: this.$t("farm.userNameAndId"),
        value: "userNameAndId",
        class: "black--text",
        align: "start",
        sortable: true,
      },
      {
        text: this.$t("farm.email"),
        value: "email",
        class: "black--text",
        align: "start",
        sortable: true,
      },
      {
        text: this.$t("farm.userRole"),
        value: "userRole",
        class: "black--text",
        align: "start",
        sortable: true,
      },
      {
        text: this.$t("farm.userRegistrationDate"),
        value: "registrationDate",
        class: "black--text",
        align: "start",
        sortable: true,
      },
      {
        text: this.$t("farm.status"),
        value: "status",
        class: "black--text",
        align: "start",
        sortable: true,
      },
    ];
    
    // Initialize selected users table headers with translations
    this.selectedUsersTableHeaders = [
      {
        text: this.$t("farm.userNameAndId"),
        value: "userNameAndId",
        class: "black--text",
        align: "start",
        sortable: true,
      },
      {
        text: this.$t("farm.email"),
        value: "email",
        class: "black--text",
        align: "start",
        sortable: true,
      },
      {
        text: this.$t("farm.userRoleAndRegDate"),
        value: "userRoleAndDate",
        class: "black--text",
        align: "start",
        sortable: true,
      },
      {
        text: this.$t("farm.status"),
        value: "status",
        class: "black--text",
        align: "start",
        sortable: true,
      },
      {
        text: "",
        value: "actions",
        class: "black--text",
        align: "center",
        sortable: false,
      },
    ];
    
    this.dialog = this.value;
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  mixins: [loadingMixin],
};
</script>

<style lang="scss" scoped>
::v-deep .add-user-dialog {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sticky-toolbar {
  position: sticky !important;
  top: 0 !important;
  z-index: 1000 !important;
}

::v-deep .user-selection-table .v-data-table-header {
  background-color: #117443 !important;
  color: white !important;
}

::v-deep .user-selection-table .v-data-table-header span {
  color: white !important;
  text-transform: uppercase !important;
}

::v-deep .user-selection-table .v-data-table__wrapper {
  border: 1px solid #e0e0e0;
}

::v-deep .user-selection-table .v-data-table tbody tr:nth-child(even) {
  background-color: #f5f5f5;
}

.green-button {
  background-color: #c1ffe7 !important;
  border: 2px solid #489e16 !important;
  color: #489e16 !important;
}

.gray-button {
  background-color: #e8e8e8 !important;
  border: 2px solid #000000 !important;
  color: #000000 !important;
}

.red-button {
  background-color: #fdebeb !important;
  border: 2px solid #da3d3d !important;
  color: #da3d3d !important;
}
</style>
